import { ScrollableContent } from 'src/components/ScrollableContent';
import { Modal, Typography, Spin, Flex, Table, Tooltip } from 'antd';
import AntDButton from 'src/components/Button';
import { ArrowLeftOutlined, FileOutlined } from '@ant-design/icons';
import { useState } from 'react';

const { Title, Text } = Typography;

const daysOfWeek = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

const ViewTimesheet = ({
  show,
  onHide,
  timesheetData,
  isLoading,
  resourceName,
}: {
  show: boolean;
  onHide: () => void;
  timesheetData?: {
    resource: string;
    startDate: string;
    endDate: string;
    submissionDate: string | null;
    approvalDate: string | null;
    rejectionDate: string | null;
    status: number;
    projects: {
      projectId: number;
      projectName: string;
      items: { date: string; duration: number }[];
      note?: string;
    }[];
  };
  isLoading: boolean;
  resourceName: string;
}) => {
  const [projectNote, setProjectNote] = useState<string>('');
  const [projectName, setProjectName] = useState<string>('');
  const [mode, setMode] = useState('timesheet'); // 'timesheet' | 'note'

  const sortTimesheetData = (timesheetData) => {
    if (!timesheetData?.projects) return timesheetData;

    return {
      ...timesheetData,
      projects: timesheetData.projects.map((project) => ({
        ...project,
        items: [...project.items].sort((a, b) => {
          return new Date(a.date).getDay() - new Date(b.date).getDay();
        }),
      })),
    };
  };

  const sortedTimesheetData = sortTimesheetData(timesheetData);

  if (!sortedTimesheetData || !sortedTimesheetData.projects.length) return null;

  const uniqueDates = sortedTimesheetData.projects[0]?.items
    .map((entry) => {
      const date = new Date(entry.date);

      return {
        formatted: date.toLocaleDateString('en-GB', {
          weekday: 'short',
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        }),
        dayIndex: date.getDay(),
      };
    })
    .sort((a, b) => a.dayIndex - b.dayIndex);

  const totalDurations = daysOfWeek.map((_, index) =>
    sortedTimesheetData.projects.reduce(
      (sum, project) => sum + (project.items[index]?.duration || 0),
      0,
    ),
  );
  const columns = [
    {
      title: '',
      dataIndex: 'projectName',
      key: 'projectName',
      width: '20%',
      render: (text, record) => (
        <>
          {record.key !== 'summaryRow' && record.note.trim() !== '' && (
            <AntDButton
              type="link"
              onClick={() => onClick(record)}
              prefixIcon={<FileOutlined />}
              htmlType="button"
              style={{ padding: 0, marginRight: 0 }}
            />
          )}
          <span>{text}</span>
        </>
      ),
    },
    {
      title: '',
      dataIndex: 'total',
      key: 'total',
      width: '10%',
      onCell: () => ({
        className: 'total-column-cell',
      }),
      render: (value, record) => <Text style={{ color: '#000' }}>{value}</Text>,
    },
    ...uniqueDates.map((date, index) => ({
      title: date.formatted,
      dataIndex: `duration_${index}`,
      key: `duration_${index}`,
      render: (value, record) => (
        <span
          style={{
            color: record.key === 'summaryRow' ? '#000' : 'rgba(0, 0, 0, 0.50)',
          }}
        >
          {value}
        </span>
      ),
    })),
  ];

  const dataSource = sortedTimesheetData.projects.map((project, idx) => {
    const row = {
      key: project.projectId || idx,
      projectName: project.projectName,
      total: project.items.reduce(
        (sum, entry) => sum + (entry?.duration || 0),
        0,
      ),
      note: project.note,
    };

    daysOfWeek.forEach((_, index) => {
      row[`duration_${index}`] = project.items[index]?.duration || 0;
    });

    return row;
  });
  const onClick = (row: any): void => {
    setProjectNote(row.note || '');
    setProjectName(row.projectName || '');
    setMode('note');
  };

  const summaryRow = {
    key: 'summaryRow',
    projectName: (
      <Text strong style={{ color: '#000' }}>
        Total
      </Text>
    ),
    total: totalDurations.reduce((sum, val) => sum + val, 0),
  };

  totalDurations.forEach((total, index) => {
    summaryRow[`duration_${index}`] = total || 0;
  });
  const dataWithSummary = [summaryRow, ...dataSource];

  return (
    <>
      <Modal
        open={show}
        onCancel={onHide}
        centered
        maskClosable={false}
        closable={mode !== 'note'}
        width={1100}
        zIndex={1200}
        footer={null}
        title={
          mode === 'timesheet' ? (
            <div
              style={{
                padding: '16px',
              }}
            >
              <Title level={4}>{resourceName} (Timesheet)</Title>
            </div>
          ) : (
            <div className="flex items-center p-4 gap-4">
              <AntDButton
                htmlType="button"
                type="text"
                onClick={() => setMode('timesheet')}
                className="flex items-center gap-1 text-[15px] font-medium text-[#343435] p-0"
                prefixIcon={<ArrowLeftOutlined />}
              ></AntDButton>

              <div className="inline-flex items-center whitespace-nowrap">
                <Title
                  level={4}
                  style={{
                    margin: 0,
                    whiteSpace: 'nowrap',
                    display: 'inline-block',
                  }}
                >
                  Note
                </Title>

                <Tooltip title={projectName}>
                  <span className="max-w-[300px] ml-3 font-normal text-gray-600 whitespace-nowrap overflow-hidden text-ellipsis">
                    ({projectName})
                  </span>
                </Tooltip>
              </div>
            </div>
          )
        }
      >
        <ScrollableContent minHeight="100%" maxHeight="100%">
          {isLoading && mode === 'timesheet' ? (
            <Flex align="center" justify="center" style={{ height: 300 }}>
              <Spin />
            </Flex>
          ) : (
            <>
              {mode === 'timesheet' && (
                <div style={{ padding: '0 1rem 1rem 1rem' }}>
                  <Table
                    rowKey="id"
                    className="custom-antd-po-table"
                    rowClassName={(record) =>
                      record.key === 'summaryRow' ? 'summary-row' : ''
                    }
                    columns={columns}
                    dataSource={dataWithSummary}
                    pagination={false}
                    rowHoverable={false}
                    bordered={false}
                    size="small"
                  />
                </div>
              )}

              {mode === 'note' && (
                <div style={{ padding: '0 1rem 1rem 1rem' }}>
                  <textarea
                    value={projectNote}
                    disabled
                    style={{
                      width: '100%',
                      height: '30vh',
                      padding: '1rem',
                      borderRadius: 8,
                    }}
                  />
                </div>
              )}
            </>
          )}
        </ScrollableContent>
      </Modal>
    </>
  );
};

export default ViewTimesheet;
